import { StrictMode, type ComponentType, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './app/App'
import { framedByAdmin, isDemo } from './config'
import { ErrorBoundary } from './ui/primitives/ErrorBoundary'
import { installStaleChunkReload } from './ui/primitives/staleChunk'
// After App on purpose: styles.css has always landed after founder.css, and today's pages rely on that order
// (.sheet's padding beats .step-sheet's). Moving these up changes the setup page.
import './ui/tokens/brand.css'
import './ui/tokens/tokens.css'
import './styles.css'

type Provider = ComponentType<{ children: ReactNode }>

// After a deploy an open tab asks for chunks that are gone: reload once onto the new build (cooldown in sessionStorage).
installStaleChunkReload()

async function boot() {
  if (isDemo() && !framedByAdmin()) {
    createRoot(document.getElementById('root')!).render(
      <main className="page">
        <section className="sheet screen-msg">
          <h1>This demo opens from the cpg.computer admin.</h1>
          <p className="lede">Ask the person showing it to you to open it from Pitch.</p>
        </section>
      </main>,
    )
    return
  }
  let Session: Provider
  let apiFetch: typeof fetch | undefined
  // Mock mode (VITE_MOCK) or the onboarding demo at /demo/onboarding. The mock is its own chunk, loaded only then,
  // and holds example data only; everywhere else production signs in through Kinde.
  if (import.meta.env.VITE_MOCK === '1' || isDemo()) {
    const m = await import('./mock')
    Session = m.MockSession
    apiFetch = m.mockFetch
  } else {
    Session = (await import('./app/KindeSession')).KindeSession
  }
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {/* The last resort: a render error anywhere above the page boundaries (sign-in, session) shows plain words, not a blank page. */}
      <ErrorBoundary>
        <Session>
          <App apiFetch={apiFetch} />
        </Session>
      </ErrorBoundary>
    </StrictMode>,
  )
}

void boot()
